import React, { useState, useEffect, useContext } from "react";
import { toast } from "react-toastify";
import {
  makeStyles,
  Button,
  Typography,
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Tooltip,
  Chip
} from "@material-ui/core";
import DeleteIcon from "@material-ui/icons/Delete";
import EditIcon from "@material-ui/icons/Edit";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import AddIcon from "@material-ui/icons/Add";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";

const useStyles = makeStyles(theme => ({
  root: {
    padding: theme.spacing(4)
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(3)
  },
  paper: {
    padding: theme.spacing(3),
    marginBottom: theme.spacing(3)
  },
  url: {
    fontFamily: "monospace",
    fontSize: 11,
    wordBreak: "break-all",
    background: "#f5f5f5",
    padding: "2px 6px",
    borderRadius: 4
  },
  formControl: {
    width: "100%",
    marginBottom: theme.spacing(2)
  },
  codePre: {
    background: "#f5f5f5",
    padding: theme.spacing(2),
    borderRadius: 4,
    fontSize: 12,
    overflowX: "auto",
    whiteSpace: "pre-wrap",
    wordBreak: "break-all"
  },
  sectionTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(1)
  },
  step: {
    marginBottom: theme.spacing(1)
  }
}));

// Garante que o value do Select seja sempre string — evita crash MUI v4
const toStr = val => (val !== null && val !== undefined ? String(val) : "");

const emptyForm = { name: "", whatsappId: "", queueId: "", tagId: "", kanbanColumnId: "" };

const WebhookForms = () => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  const [forms, setForms] = useState([]);
  const [whatsapps, setWhatsapps] = useState([]);
  const [queues, setQueues] = useState([]);
  const [tags, setTags] = useState([]);
  const [kanbanColumns, setKanbanColumns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState(emptyForm);

  useEffect(() => {
    loadAll();
  }, []);

  const loadAll = async () => {
    setLoading(true);

    try {
      const res = await api.get("/webhook-forms");
      setForms(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      toastError(err);
    }

    try {
      const res = await api.get("/whatsapp/");
      const data = res.data;
      setWhatsapps(Array.isArray(data) ? data : data?.whatsapps || []);
    } catch (err) {
      console.error("[WebhookForms] Erro ao carregar whatsapps:", err);
    }

    try {
      const res = await api.get("/queue");
      setQueues(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("[WebhookForms] Erro ao carregar filas:", err);
    }

    // Tags normais (kanban=0)
    try {
      const res = await api.get("/tags");
      const data = res.data;
      setTags(Array.isArray(data) ? data : data?.tags || []);
    } catch (err) {
      console.error("[WebhookForms] Erro ao carregar tags:", err);
    }

    // Colunas do Kanban — endpoint dedicado retorna { lista: [...] }
    try {
      const [kanbanRes, laneRes] = await Promise.all([
        api.get("/tag/kanban"),
        api.get("/company-kanban/lane-order").catch(() => ({ data: null }))
      ]);
      const cols = kanbanRes.data?.lista || [];
      // Ordena pelas colunas na ordem do Pipeline (laneOrder é array de IDs)
      const laneOrder = laneRes.data?.laneOrder;
      if (Array.isArray(laneOrder) && laneOrder.length > 0) {
        const orderMap = {};
        laneOrder.forEach((id, idx) => { orderMap[String(id)] = idx; });
        cols.sort((a, b) => {
          const ia = orderMap[String(a.id)] ?? 999;
          const ib = orderMap[String(b.id)] ?? 999;
          return ia - ib;
        });
      }
      setKanbanColumns(cols);
    } catch (err) {
      console.error("[WebhookForms] Erro ao carregar colunas kanban:", err);
    }

    setLoading(false);
  };

  const handleOpenCreate = () => {
    setEditing(null);
    setFormData(emptyForm);
    setModalOpen(true);
  };

  const handleOpenEdit = item => {
    setEditing(item);
    setFormData({
      name: item.name || "",
      whatsappId: item.whatsappId ? String(item.whatsappId) : "",
      queueId: item.queueId ? String(item.queueId) : "",
      tagId: item.tagId ? String(item.tagId) : "",
      kanbanColumnId: item.kanbanColumnId ? String(item.kanbanColumnId) : ""
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error("Informe um nome para a conexão");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: formData.name.trim(),
        whatsappId: formData.whatsappId ? Number(formData.whatsappId) : null,
        queueId: formData.queueId ? Number(formData.queueId) : null,
        tagId: formData.tagId ? Number(formData.tagId) : null,
        kanbanColumnId: formData.kanbanColumnId ? Number(formData.kanbanColumnId) : null
      };

      if (editing) {
        await api.put(`/webhook-forms/${editing.id}`, payload);
        toast.success("Conexão atualizada com sucesso!");
      } else {
        await api.post("/webhook-forms", payload);
        toast.success("Conexão criada com sucesso!");
      }
      setModalOpen(false);
      loadAll();
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async id => {
    if (!window.confirm("Deseja remover esta conexão de site?")) return;
    try {
      await api.delete(`/webhook-forms/${id}`);
      toast.success("Conexão removida!");
      loadAll();
    } catch (err) {
      toastError(err);
    }
  };

  const handleCopyUrl = token => {
    const url = `${backendUrl}/webhook-forms/receive/${token}`;
    navigator.clipboard
      .writeText(url)
      .then(() => toast.success("URL copiada para a área de transferência!"));
  };

  const getWebhookUrl = token => `${backendUrl}/webhook-forms/receive/${token}`;

  const findTag = tagId => [...tags, ...kanbanColumns].find(t => String(t.id) === String(tagId));
  const findWp = wpId => whatsapps.find(w => String(w.id) === String(wpId));
  const findQueue = qId => queues.find(q => String(q.id) === String(qId));
  const findKanbanCol = colId => kanbanColumns.find(t => String(t.id) === String(colId));

  const sampleCode = token =>
    `fetch("${getWebhookUrl(token || "SEU_TOKEN")}", {
  method: "POST",
  headers: { "Content-Type": "application/json" },
  body: JSON.stringify({
    name: "João Silva",
    phone: "5511999999999",
    email: "joao@email.com",
    origem: "Página de vendas"   // campo extra → salvo em Outras Informações
  })
});`;

  return (
    <Container className={classes.root} maxWidth="lg">
      {/* Cabeçalho */}
      <div className={classes.header}>
        <div>
          <Typography variant="h5" style={{ fontWeight: 700 }}>
            Conexões de Sites
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Receba leads de formulários do seu site diretamente no CRM
          </Typography>
        </div>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={handleOpenCreate}
        >
          Nova Conexão
        </Button>
      </div>

      {/* Tabela */}
      <Paper className={classes.paper}>
        {loading ? (
          <div style={{ display: "flex", justifyContent: "center", padding: 32 }}>
            <CircularProgress />
          </div>
        ) : (
          <Table>
            <TableHead>
              <TableRow>
                <TableCell><strong>Nome</strong></TableCell>
                <TableCell><strong>URL do Webhook</strong></TableCell>
                <TableCell><strong>WhatsApp</strong></TableCell>
                <TableCell><strong>Fila</strong></TableCell>
                <TableCell><strong>Tag</strong></TableCell>
                <TableCell><strong>Coluna Kanban</strong></TableCell>
                <TableCell align="right"><strong>Ações</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {forms.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center" style={{ padding: 32, color: "#888" }}>
                    Nenhuma conexão cadastrada. Clique em "Nova Conexão" para começar.
                  </TableCell>
                </TableRow>
              )}
              {forms.map(f => {
                const tag = findTag(f.tagId);
                const kanbanCol = findKanbanCol(f.kanbanColumnId);
                const wp = findWp(f.whatsappId);
                const queue = findQueue(f.queueId);
                return (
                  <TableRow key={f.id} hover>
                    <TableCell>
                      <Typography variant="body2" style={{ fontWeight: 500 }}>
                        {f.name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                        <span className={classes.url}>{getWebhookUrl(f.token)}</span>
                        <Tooltip title="Copiar URL">
                          <IconButton size="small" onClick={() => handleCopyUrl(f.token)}>
                            <FileCopyIcon style={{ fontSize: 14 }} />
                          </IconButton>
                        </Tooltip>
                      </div>
                    </TableCell>
                    <TableCell>
                      {wp ? wp.name : <span style={{ color: "#aaa" }}>Padrão</span>}
                    </TableCell>
                    <TableCell>
                      {queue ? queue.name : <span style={{ color: "#aaa" }}>—</span>}
                    </TableCell>
                    <TableCell>
                      {tag ? (
                        <Chip
                          label={tag.name}
                          size="small"
                          style={{ background: tag.color || "#888", color: "#fff" }}
                        />
                      ) : (
                        <span style={{ color: "#aaa" }}>—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {kanbanCol ? (
                        <Chip
                          label={kanbanCol.name}
                          size="small"
                          style={{ background: kanbanCol.color || "#1976d2", color: "#fff" }}
                        />
                      ) : (
                        <span style={{ color: "#aaa" }}>Novos Leads</span>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Editar">
                        <IconButton size="small" onClick={() => handleOpenEdit(f)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Remover">
                        <IconButton size="small" color="secondary" onClick={() => handleDelete(f.id)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </Paper>

      {/* Guia de uso */}
      <Paper className={classes.paper}>
        <Typography variant="h6" className={classes.sectionTitle}>
          Como usar
        </Typography>
        <Typography variant="body2" className={classes.step}>
          <strong>1.</strong> Crie uma nova conexão e configure qual conta WhatsApp vai receber os leads.
        </Typography>
        <Typography variant="body2" className={classes.step}>
          <strong>2.</strong> Copie a URL do webhook gerada.
        </Typography>
        <Typography variant="body2" className={classes.step}>
          <strong>3.</strong> No código do formulário do seu site, faça um POST para essa URL com os dados em JSON.
        </Typography>
        <Typography variant="body2" className={classes.step} style={{ marginBottom: 12 }}>
          <strong>Campos reconhecidos:</strong>{" "}
          <code>name</code> / <code>nome</code> — <code>phone</code> / <code>telefone</code> / <code>celular</code> — <code>email</code>.{" "}
          Qualquer outro campo será salvo em <em>"Outras Informações"</em> do contato.
        </Typography>
        <Typography variant="body2" style={{ fontWeight: 600, marginBottom: 4 }}>
          Exemplo de código (JavaScript):
        </Typography>
        <pre className={classes.codePre}>{sampleCode(forms[0]?.token)}</pre>
      </Paper>

      {/* Modal criar/editar */}
      <Dialog open={modalOpen} onClose={() => setModalOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>
          {editing ? "Editar Conexão de Site" : "Nova Conexão de Site"}
        </DialogTitle>
        <DialogContent>
          <TextField
            label="Nome da conexão"
            fullWidth
            variant="outlined"
            margin="normal"
            value={formData.name}
            onChange={e => { const v = e.target.value; setFormData(f => ({ ...f, name: v })); }}
            placeholder='Ex: "Formulário Site Natural Bot"'
            helperText="Apelido interno para identificar de qual formulário vem o lead."
            autoFocus
          />

          <FormControl variant="outlined" className={classes.formControl} style={{ marginTop: 8 }}>
            <InputLabel id="wp-label">Conexão WhatsApp</InputLabel>
            <Select
              labelId="wp-label"
              label="Conexão WhatsApp"
              value={toStr(formData.whatsappId)}
              onChange={e => { const v = e.target.value; setFormData(f => ({ ...f, whatsappId: v })); }}
            >
              <MenuItem value=""><em>Padrão da empresa</em></MenuItem>
              {whatsapps.filter(Boolean).map(w => (
                <MenuItem key={w.id} value={String(w.id)}>
                  {w.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl variant="outlined" className={classes.formControl}>
            <InputLabel id="queue-label">Fila</InputLabel>
            <Select
              labelId="queue-label"
              label="Fila"
              value={toStr(formData.queueId)}
              onChange={e => { const v = e.target.value; setFormData(f => ({ ...f, queueId: v })); }}
            >
              <MenuItem value=""><em>Nenhuma fila</em></MenuItem>
              {queues.filter(Boolean).map(q => (
                <MenuItem key={q.id} value={String(q.id)}>
                  {q.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl variant="outlined" className={classes.formControl}>
            <InputLabel id="tag-label">Tag (etiqueta)</InputLabel>
            <Select
              labelId="tag-label"
              label="Tag (etiqueta)"
              value={toStr(formData.tagId)}
              onChange={e => { const v = e.target.value; setFormData(f => ({ ...f, tagId: v })); }}
            >
              <MenuItem value=""><em>Nenhuma tag</em></MenuItem>
              {tags.filter(Boolean).map(t => (
                <MenuItem key={t.id} value={String(t.id)}>
                  {t.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl variant="outlined" className={classes.formControl}>
            <InputLabel id="kanban-label" shrink>Coluna do Kanban</InputLabel>
            <Select
              labelId="kanban-label"
              label="Coluna do Kanban"
              displayEmpty
              notched
              value={toStr(formData.kanbanColumnId)}
              onChange={e => { const v = e.target.value; setFormData(f => ({ ...f, kanbanColumnId: v })); }}
            >
              <MenuItem value=""><em>Novos Leads</em></MenuItem>
              {kanbanColumns.filter(Boolean).map(col => (
                <MenuItem key={col.id} value={String(col.id)}>
                  {col.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setModalOpen(false)}
            color="secondary"
            variant="outlined"
            disabled={saving}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleSave}
            color="primary"
            variant="contained"
            disabled={saving || !formData.name.trim()}
          >
            {saving ? <CircularProgress size={20} /> : editing ? "Salvar" : "Criar"}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default WebhookForms;
